import api from "../../../api";
import { getLocalDateString } from "../../../util/formatDate";

export const STATUS_LIST = [
    "Pendiente",
    "Recordatorio enviado",
    "Confirmado",
    "Cancelado",
    "Recibido",
];

export const STATUS_STYLES = [
    "text-yellow-800 bg-yellow-100",
    "text-blue-800 bg-blue-100",
    "text-green-800 bg-green-100",
    "text-red-800 bg-red-100",
    "text-purple-800 bg-purple-100",
];

export const APPOINTMENT_TYPE_LIST = ["Consulta", "Estudio"];

export const AVAILABILITY_MAP = {
    reservado: {
        text: "Reservado",
        style: "text-yellow-800 bg-yellow-100",
    },
    disponible: {
        text: "Disponible",
        style: "text-green-800 bg-green-100",
    },
};

export const getAppointmentItemKey = (appt) =>
    appt.id ?? `free-${appt.date ?? "sin-fecha"}-${appt.time ?? "sin-hora"}`;

export const formatAppointmentDate = (dateStr) => {
    if (!dateStr) return "Sin fecha";
    const [y, m, d] = dateStr.split("-");
    return `${d}/${m}/${y}`;
};

export const formatAppointmentTime = (timeStr) => {
    if (!timeStr) return "Sin agendamiento";
    return timeStr.split(":").slice(0, 2).join(":");
};

export const formatLongAppointmentDate = (date) => {
    if (!date) return "Sin fecha";

    const dateObj = new Date(date);
    const fixed = new Date(
        dateObj.getUTCFullYear(),
        dateObj.getUTCMonth(),
        dateObj.getUTCDate(),
    );

    let formatted = new Intl.DateTimeFormat("es-ES", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
    }).format(fixed);

    formatted = formatted.charAt(0).toUpperCase() + formatted.slice(1);
    return formatted.replace(",", " del");
};

export const formatAppointmentHistoryDate = (timestamp) =>
    new Date(timestamp).toLocaleString("es-PY", {
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
    });

export const getAppointmentStatusMeta = (status) => {
    if (!status) return null;

    return {
        text: STATUS_LIST[status - 1] ?? "Sin estado",
        style: STATUS_STYLES[status - 1] ?? "text-gray-700 bg-gray-100",
    };
};

export const getAppointmentAvailabilityMeta = (appt) => {
    if (appt.id && !appt.patient) {
        return {
            text: "Bloqueado",
            style: "text-red-800 bg-red-100",
        };
    }

    return (
        AVAILABILITY_MAP[appt.availability] ?? {
            text: "Sin disponibilidad",
            style: "text-gray-700 bg-gray-100",
        }
    );
};

export const getAppointmentPatientLabel = (appt) => {
    if (!appt.patient) {
        if (appt.id) return "Horario bloqueado";
        return "Horario disponible";
    }

    const insurance = appt.patient.insuranceName || "---";
    return `${appt.patient.firstName} ${appt.patient.lastName} (${insurance})`;
};

export const getAppointmentDoctorLabel = (appt) => {
    if (!appt.doctor) return "Sin doctor";
    return `${appt.doctor.firstName} ${appt.doctor.lastName}`;
};

const isAppointmentHighlighted = (appt) =>
    !appt.patient || appt.patient.hasConsultations;

export const getAppointmentRowClassName = (appt) =>
    isAppointmentHighlighted(appt)
        ? "bg-blue-100 dark:bg-blue-900/40"
        : "bg-gray-100 dark:bg-gray-800/60";

export const getAppointmentHoverClassName = (appt) =>
    isAppointmentHighlighted(appt)
        ? "hover:bg-blue-200 dark:hover:bg-blue-900"
        : "hover:bg-gray-200 dark:hover:bg-gray-700";

export const getAppointmentStatusHoverClassName = (appt) =>
    isAppointmentHighlighted(appt)
        ? "hover:bg-blue-200 dark:hover:bg-blue-800"
        : "hover:bg-gray-200 dark:hover:bg-gray-700";

export const getAppointmentCardTone = (appt) => {
    if (isAppointmentHighlighted(appt)) {
        return {
            card: "border-blue-200 bg-blue-50 dark:border-blue-800/60 dark:bg-blue-950/40",
            hover: "hover:bg-blue-100/80 dark:hover:bg-blue-900/50",
            section: "border-white/80 bg-white/70 dark:border-blue-900/70 dark:bg-gray-900/50",
        };
    }

    return {
        card: "border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900",
        hover: "hover:bg-gray-50 dark:hover:bg-gray-800/70",
        section: "border-gray-200/80 bg-gray-50/80 dark:border-gray-700 dark:bg-gray-800/60",
    };
};

export const toErrorMessages = (errorMessage) => {
    if (!errorMessage) return [];
    if (typeof errorMessage === "string") return [errorMessage];
    if (Array.isArray(errorMessage)) return errorMessage;
    return Object.values(errorMessage).flat();
};

export const getAppointmentErrorMessage = (error) => {
    if (error?.code === "serverError") {
        return "Error 500: Error del servidor";
    }

    return error?.message || "Ocurrió un error";
};

export const isTurneroDisabled = (appt) => {
    const formattedToday = getLocalDateString();
    return (
        appt?.status === 5 ||
        appt?.status === 4 ||
        appt?.date !== formattedToday
    );
};

export const addAppointmentToTurns = async ({
    appt,
    notify,
    onSave,
    isUrgent = false,
}) => {
    await api.turns.create({
        doctorGovId: appt.doctor.govId,
        patientGovId: appt.patient.govId,
        isUrgent,
    });

    notify?.("Turno creado correctamente", "success");

    await api.appointments.update(
        appt.id,
        {
            ...appt,
            doctor: appt.doctor.id,
            patient: appt.patient.id,
            status: 5,
        },
        true,
    );

    onSave?.();
    notify?.("Cita actualizada correctamente", "success");
};

export const updateAppointmentStatus = async ({
    appt,
    data,
    notify,
    onSave,
}) => {
    if (!appt?.id) return;

    await api.appointments.update(appt.id, data, true);
    onSave?.();
    notify?.("Estado actualizado", "success");
};

export const toggleAppointmentScheduleLock = async ({
    appt,
    notify,
    onSave,
}) => {
    if (appt.id) {
        await api.appointments.delete(appt.id);
        notify?.("Horario desbloqueado correctamente", "success");
    } else {
        await api.appointments.create({
            ...appt,
            doctor: appt.doctor?.id || null,
        });
        notify?.("Horario bloqueado correctamente", "success");
    }

    onSave?.();
};
